import useCalendar from '@/hooks/calendar/useCalendar';
import useCollections from '@/hooks/collections/useCollections';
import useSpecies from '@/hooks/species/useSpecies';
import { ReactNode, useMemo, useState } from 'react';
import identifyMediaWithWrongMimeType from './errors/identifyMediaWithWrongMimeType';
import identifyMissingAltText from './errors/identifyMissingAltText';
import identifyMissingDescription from './errors/identifyMissingDescription';
import identifyMissingFromCalendar from './errors/identifyMissingFromCalendar';
import identifyMissingFromCollections from './errors/identifyMissingFromCollections';
import identifyTooLongAltText from './errors/identifyTooLongAltText';

// All the error identifier functions to use
const identifierFunctions = [
	identifyMissingAltText,
	identifyTooLongAltText,
	identifyMissingFromCalendar,
	identifyMissingFromCollections,
	identifyMissingDescription,
	identifyMediaWithWrongMimeType,
];

export interface EditorialError {
	title: string;
	description: ReactNode;
	speciesId: number;
}

export type EditorialErrorIdentifier = (data: {
	useSpecies: ReturnType<typeof useSpecies>;
	useCalendar: ReturnType<typeof useCalendar>;
	useCollections: ReturnType<typeof useCollections>;
}) => EditorialError[];

const EditorialErrors = () => {
	const species = useSpecies();
	const calendar = useCalendar();
	const collections = useCollections();

	const [onlyIncludeCalendarSpecies, setOnlyIncludeCalendarSpecies] =
		useState(false);

	const errors = useMemo(() => {
		if (!species.species || !calendar.calendar || !collections.collections)
			return [];

		const errors: EditorialError[] = [];

		// Go through each error identifier function and combine the results
		identifierFunctions.forEach((identifier) => {
			errors.push(
				...identifier({
					useSpecies: species,
					useCalendar: calendar,
					useCollections: collections,
				}),
			);
		});

		// Add a unique ID to each error
		return errors
			.map((error) => ({
				...error,
				id: Math.random().toString(36),
			}))
			.filter((error) => {
				if (onlyIncludeCalendarSpecies) {
					return calendar.getIsSpeciesInCalendar(error.speciesId);
				}
				return true;
			});
	}, [species, calendar, collections, onlyIncludeCalendarSpecies]);

	if (errors.length === 0) return null;

	return (
		<section className="select-text">
			<h2 className="text-xl">
				Errors in redactionele inhoud ({errors.length})
			</h2>
			<label className="flex gap-2 p-2">
				<input
					type="checkbox"
					className="cursor-pointer"
					checked={onlyIncludeCalendarSpecies}
					onChange={() =>
						setOnlyIncludeCalendarSpecies(
							!onlyIncludeCalendarSpecies,
						)
					}
				/>
				Alleen soorten van de kalender
			</label>
			<ol className="grid list-inside list-decimal gap-4 lg:grid-cols-2 xl:grid-cols-3">
				{errors.map((error, index) => (
					<li
						key={`${error.id}-${error.speciesId}-${index}`}
						className="rounded-lg bg-slate-100 p-2">
						<h3 className="text-lg">{error.title}</h3>
						<p>{error.description}</p>
					</li>
				))}
			</ol>
		</section>
	);
};

export default EditorialErrors;
